import {
  useCallback, useEffect, useId, useRef, useState,
  type CSSProperties, type JSX, type ReactNode,
} from 'react'
import {
  ApiError, SESSION_ENDED, api,
  type ConfigBuildingType, type ConfigDocument, type ConfigFactorRow, type ConfigVersion,
  type BlastRadiusPreview, type EndUse, type Strategy, type StrategyLibrary,
  type StrategyLibraryInForce,
} from '../api.js'
import { BlastRadius } from '../components/BlastRadius.js'
import { TimeTravel } from '../components/TimeTravel.js'
import { asWords, formatDate } from '../components/Pill.js'
import { T } from '../tokens.js'

/**
 * The program manager's configuration console (R-12, R-13, R-23, spec §7.8) —
 * the wireframe's artboard **1f**, *"Program configuration — curtailment
 * factors + blast-radius preview"*.
 *
 * ## The blast radius is in the commit path, not beside it
 *
 * There is exactly one way from an edit to a commit and it runs through the
 * preview. `Review changes` is the only control that moves the console
 * forward; `Commit configuration` **does not exist in the DOM** until a preview
 * has come back, and it disappears again the moment the draft changes under it.
 * That is stronger than disabling it: a control a hurried user can see is a
 * control they will look for a way around.
 *
 * The preview is never allowed to be stale. Every render compares the document
 * on screen against the document the preview was computed for, and a mismatch
 * discards the preview outright rather than dimming it. Showing yesterday's
 * numbers under today's edit is the failure this whole screen exists to
 * prevent, and a greyed-out panel is still a panel with numbers on it.
 *
 * ## Why the preview is a button and the panel is always there
 *
 * 1f draws the panel always-on. It costs ≈62 ms per account
 * (`config/blast-radius.ts`, measured), so an exhaustive preview over the
 * seeded book's 313 in-scope accounts is ≈40 s — not a thing that can run on
 * every keystroke, and not a thing to run silently. So the *frame* is always on
 * screen, with the sentence that says what it will tell you and what it costs;
 * the *numbers* are asked for. {@link PREVIEW_LIMIT} accounts is about three
 * seconds, and the panel says so before the wait rather than after it.
 *
 * ## Every surface is on one scrolling page, and that is deliberate
 *
 * The wireframe draws five tabs. Tabs would put four-fifths of a draft out of
 * sight while the blast radius counts all of it — a program manager who edited
 * a rate on Tuesday and a shed factor on Wednesday would commit both while
 * looking at one. The section nav scrolls rather than swaps, the right rail is
 * sticky, and `What this draft changes` in the panel is the server's own diff
 * over the whole document, so nothing a commit carries is ever off screen.
 *
 * `initialTab` names the section to open on, and `strategies` is the value that
 * makes it more than decoration.
 *
 * ## The sixth surface commits down a different pipe, and that is stated
 *
 * Brief §4.7 enumerates six configuration surfaces. Five of them are sections
 * of one `ConfigDocument` and travel out through `POST /api/config/commit`. The
 * sixth — the curtailment strategy library — is a **separate effective-dated
 * store** (`strategy_library_versions`), committed through
 * `POST /api/strategies`, and its two ranking knobs (`strategyWeights`,
 * `negligibleShareThreshold`) are back in the configuration document.
 *
 * So the strategy tab carries two commit paths, and the screen says which is
 * which rather than drawing one button that means two things. The knobs go
 * through the rail with everything else; the library has its own commit block,
 * its own effective date and its own confirmation, inside the section.
 *
 * ## Neither path has a blast radius, and the two land at opposite times
 *
 * `computeBlastRadius` scores estimates. A ranking weight moves no kW, and the
 * server already lists `strategyWeights` and `negligibleShareThreshold` in
 * `CONFIG_PATHS_NOT_EVALUATED`, so the panel's own `blindTo` block names them
 * above the headline. A **library** commit is not a `ConfigDocument` at all and
 * there is no preview route for it.
 *
 * What the wireframe, the plan and this file's own first draft all missed is
 * **when** each one takes effect, and the two are opposite. `rankForAccount`
 * pins the *configuration* to the version stamped on the account's estimate
 * (R-22) and resolves the *library* on the date. So committing a weight
 * reorders nothing until the next scoring run, while committing a library
 * reorders lists immediately. Both figures are measured; see
 * {@link RANKING_NOT_MEASURED} and {@link LIBRARY_NOT_PREVIEWABLE}, which say
 * so at the controls, before a preview is ever run.
 *
 * ## What this screen does not do
 *
 * It does not diff. It does not score. It does not decide whether a document is
 * valid. All three are the server's (`config/draft.ts`, `config/blast-radius.ts`,
 * `validateConfig`), and a browser-side second opinion on any of them is a
 * second answer to a question that already has one (R-17). The only arithmetic
 * here is a histogram's bar heights, which are a scale rather than a figure.
 *
 * ## A document this file does not fully describe still commits intact
 *
 * `ConfigDocument` in `api.ts` names the five sections the console edits, not
 * the sixteen the document carries. Every edit below is a **spread** of the
 * document the server sent, so the eleven unnamed sections travel out exactly
 * as they came in. See that interface for why the transcription is partial.
 */

/** All six surfaces of brief §4.7. `strategies` is the sixth (Task 7a). */
export type ConsoleSection
  = 'season' | 'rates' | 'baseline' | 'factors' | 'buildingTypes' | 'strategies'

const SECTIONS: readonly { id: ConsoleSection; label: string }[] = [
  { id: 'season', label: 'Season & windows' },
  { id: 'rates', label: 'Rates & terms' },
  { id: 'baseline', label: 'Baseline method' },
  { id: 'factors', label: 'Curtailment factors' },
  { id: 'buildingTypes', label: 'Building types' },
  { id: 'strategies', label: 'Strategy library' },
]

/**
 * The section named by a `#config-<id>` fragment, or `null`.
 *
 * The nav entries have always been plain `<a href="#config-…">` — the browser
 * does the scrolling and the back button works — so the fragment is already the
 * record of which surface the reader asked for. Reading it back is what turns
 * the current-section mark from a prop nobody passes into something that
 * follows the page.
 */
function sectionFromHash(hash: string): ConsoleSection | null {
  const id = hash.replace(/^#config-/, '')
  return SECTIONS.some((section) => section.id === id) ? id as ConsoleSection : null
}

export const CONFIG_HEADING = 'Program configuration'

/**
 * How many accounts a preview scores.
 *
 * **Measured against the seeded book on this machine: 3.7 s at 25, 47.5 s
 * exhaustive over all 313.** The plan sized the budget at 25 on an estimate of
 * 2.7 s; the real figure is a second longer, which is why the copy says four
 * seconds rather than three. Long enough to need saying, short enough that a
 * program manager will actually wait for it.
 *
 * The route refuses to default this, precisely so a caller cannot be handed a
 * sample it did not ask for — and the panel states the sample beside its own
 * headline, because 25 of 313 is 8% of the book and a reader who takes the
 * count for a census under-reads the change by an order of magnitude.
 */
export const PREVIEW_LIMIT = 25

export const CONFIG_LOAD_FAILED = 'The configuration could not be loaded. Try again.'
export const CONFIG_FORBIDDEN
  = 'Program configuration is managed by the program team. Your account cannot open it.'
export const PREVIEW_FAILED = 'The blast radius could not be computed, so nothing was checked. Try again.'
export const PREVIEW_RUNNING = `Scoring ${PREVIEW_LIMIT} accounts against this draft — about four seconds.`
export const NOTHING_TO_REVIEW = 'This draft is the configuration in force. Change something to review it.'
export const DRAFT_SAVED = 'Draft saved. It is yours alone and nothing else reads it until you commit.'
export const DRAFT_SAVE_FAILED = 'That draft could not be saved. Try again.'
export const COMMIT_FAILED = 'That configuration was not committed.'

/**
 * What the commit-problem block is handed when a commit is refused.
 *
 * `POST /api/config/commit` refuses an invalid document with
 * `{ error: 'invalid_configuration', errors: string[] }` and **no `message`**
 * — `validateConfig`'s own list, one entry per bad field, the same reasons
 * `commitConfig` would have refused the write with. Those reasons used to reach
 * this screen and stop: `ApiError` knew only `{ error, message }`, so `detail`
 * came back `null` and a program manager whose commit was rejected for five
 * named fields was shown *"The server refused that request."* — one sentence
 * naming none of them, with no way to find out which five. The block they were
 * shown it in has rendered a `<ul>` of `<li>`s since the day it was written.
 *
 * So the list is what goes in when there is one, in the server's order, whole.
 * `detail` is the single-sentence fallback for the refusals that carry one
 * instead (a 403, a 409), and `fallback` is the last resort for a transport
 * failure that is not an `ApiError` at all. Every branch produces at least one
 * line: a refusal that says nothing useful must still say something.
 *
 * Both callers are refusals of the *same document* down two routes — the commit
 * and the blast-radius preview, which validates before it scores and refuses
 * with the identical body — so they share this rather than each deciding again
 * which of three fields to read.
 */
function refusalReasons(error: unknown, fallback: string): string[] {
  if (!(error instanceof ApiError)) return [fallback]
  if (error.reasons.length > 0) return [...error.reasons]
  return [error.detail ?? error.message]
}

/**
 * The strategy tab's central statement, pinned whole by the test because a
 * `toContain` on a fragment of it could not see it being softened.
 *
 * ## It says two things, and the second was measured rather than reasoned
 *
 * The first half is R-13's honesty clause: the blast radius counts accounts
 * whose *estimate* moved, a ranking weight moves no estimate, and the server
 * agrees in machine-readable form — `strategyWeights` and
 * `negligibleShareThreshold` are both in `CONFIG_PATHS_NOT_EVALUATED`, so
 * `BlastRadius` draws them under *"Not evaluated by this preview"* above the
 * headline.
 *
 * The second half is the one an earlier draft of this file got **wrong**, and
 * it was wrong in the direction that matters. It said a weights change
 * *"reorders every rep's strategy list"*. It does not. `rankForAccount`
 * (`strategies/rank.ts`) reads
 * `getConfigVersion(db, current.configVersionId).document` whenever the account
 * has an estimate — the version **stamped on that estimate**, not the one in
 * force — which is R-22 working exactly as designed: a list must be explicable
 * against the configuration its figures were computed under. The consequence
 * for this screen is that committing either knob changes nothing anyone can
 * see until the next scoring run re-stamps the book.
 *
 * **Measured, against a copy of the seeded book, through this console.**
 * `strategyWeights` driven from `{ yield: 0.3, impact: 0.5, feasibility: 0.2 }`
 * to `{ 1, 0, 0 }` — the extreme — committed and in force: **0 of 60 accounts
 * reordered**. `negligibleShareThreshold` 0.05 → 0.1, committed: **0 of 60**
 * on the not-applicable set, the order and the indicative total alike. A
 * program manager who was told this reorders every list, saw a zero blast
 * radius, committed anyway and then went looking at accounts would find
 * nothing and have no way to tell which of the two claims was the lie.
 */
export const RANKING_NOT_MEASURED
  = 'No preview covers these two, and neither takes effect on commit. The blast radius scores '
  + 'estimates, and a ranking weight moves no kW. And an account’s strategy list is ranked against '
  + 'the configuration version stamped on its estimate, not the one in force — so committing these '
  + 'reorders nothing until the next scoring run re-scores the book.'

/**
 * Said at the library's own commit block, which is a different pipe again — and
 * which behaves the **opposite** way from the two knobs above.
 *
 * `POST /api/config/draft/blast-radius` takes a `ConfigDocument`. A library is
 * not one, and there is no second preview route, so this commit is the one act
 * on this screen with no numbers in front of it.
 *
 * And it lands immediately, where a configuration commit does not:
 * `rankForAccount` resolves the library with
 * `resolveStrategyLibraryAt(db, asOf)` — **on the date**, never pinned to the
 * estimate — while it pins the *configuration* to the estimate's stamp. The
 * asymmetry is the reverse of what a reader would guess from the fact that both
 * stores are effective-dated and append-only, so it is stated rather than left
 * to be discovered by a program manager who moved the wrong one.
 *
 * **Measured, through this console:** one strategy's `impact` changed from
 * `high` to `low` and committed, with no scoring run, re-stamped
 * `estimates.strategy_library_version_id` on **60 of 60** accounts and changed
 * the ranked order on **10 of 60**.
 */
export const LIBRARY_NOT_PREVIEWABLE
  = 'A library commit has no preview at all, and unlike the two controls above it lands at once: '
  + 'the library is resolved on the date rather than pinned to each estimate. It changes the ranked '
  + 'strategy list every rep sees on every account the moment it is in force, and nothing here can '
  + 'score that beforehand.'

/** Spec §2 defers bulk import and its dry-run diff to roadmap RM-4. Said, not left blank. */
export const BULK_IMPORT_NOT_IN_PILOT
  = 'Bulk import is not in this pilot. Strategies are created and edited one at a time here.'

/**
 * The negligible-share control's label, in the program's terms rather than the
 * field's. Task 0 made `negligibleShareThreshold` configuration so that this
 * control could exist; a field no surface can change is worse than a constant.
 */
export const NEGLIGIBLE_SHARE_LABEL
  = 'Ignore end uses below this share of the account’s curtailable load'

/**
 * How far this one number reaches, in accounts, said where it is edited.
 *
 * This slot used to warn that `describeBreakdown` in
 * `packages/engine/src/curtailment.ts` filtered on a literal `share >= 0.05`
 * and so would disagree with this control the moment it moved. Two things were
 * wrong with that sentence and both were measured rather than argued:
 *
 * - It named **the one-pager**. The narrative never reaches a customer:
 *   `http/presentation.ts` drops it from the presentation payload and no screen
 *   in `packages/web` renders it. The warning described a document that does
 *   not carry the string it was about.
 * - It ran **one way**, warning only about *raising* the cut-off. Lowering it
 *   diverges just as far and in more accounts.
 *
 * `describeBreakdown` now takes this number, so there is no disagreement left
 * to warn about. What is worth saying at the field is the size of the move,
 * which nothing on this screen said: the figures below are exhaustive over the
 * 179 non-empty of the 313 stored `endUseBreakdown`s, and they are the reason
 * this is a two-decimal field rather than a slider.
 */
export const NEGLIGIBLE_SHARE_REACH
  = 'One number, two surfaces: this cut-off also decides which end uses the stored “material '
  + 'load” narrative names, so the two cannot disagree. Measured on the current book, raising it '
  + 'to 0.10 drops 206 end uses across 103 accounts below the line and 0.20 drops 374 across 139; '
  + 'lowering it to 0.02 brings 437 across 179 above it. Both land on the next run.'

export const NO_STRATEGY_LIBRARY_IN_FORCE
  = 'No strategy library is in force. Nothing can be ranked for any account until one is committed '
  + '— this is a library waiting to be written, not a failure.'

export const LIBRARY_COMMIT_FAILED = 'That strategy library was not committed.'

/**
 * The day the console resolves everything against — the browser's own calendar
 * day, in local parts rather than through `toISOString`, which is UTC and would
 * name tomorrow every Phoenix evening after 5pm.
 *
 * It is used for two things and it matters that it is the *same* value for
 * both: the base document is `GET /api/config/at?date=<this>` and the preview's
 * `asOfDate` is `<this>` too. Letting the preview fall back to the server's own
 * day would, on the one day a version becomes effective, score the "before"
 * side against a version other than the one on screen — and the whole claim of
 * the panel is that the difference between its two sides is the draft and
 * nothing else.
 */
function today(): string {
  const now = new Date()
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`
}

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  fontWeight: T.weight.semi,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  color: T.color.micro,
}

const field: CSSProperties = {
  border: `1px solid ${T.color.border}`,
  borderRadius: T.radius.square,
  padding: '7px 8px',
  fontFamily: T.fontFamily,
  fontSize: 13.5,
  color: T.color.ink,
  background: '#fff',
  width: '100%',
}

const buttonBase: CSSProperties = {
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  padding: '11px 14px',
  cursor: 'pointer',
}

const secondaryButton: CSSProperties = {
  ...buttonBase,
  border: `1.5px solid ${T.color.border}`,
  background: '#fff',
  color: T.color.ink,
}

const primaryButton: CSSProperties = {
  ...buttonBase,
  border: `1.5px solid ${T.color.navy}`,
  background: T.color.navy,
  color: '#fff',
}

const dangerButton: CSSProperties = {
  ...buttonBase,
  border: `1.5px solid ${T.color.red}`,
  background: T.color.red,
  color: '#fff',
}

const sectionCard: CSSProperties = {
  background: '#fff',
  border: `1.5px solid ${T.color.rule}`,
  padding: '16px 18px',
  marginBottom: 18,
}

const gridRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
  gap: 14,
  marginTop: 12,
}

const help: CSSProperties = {
  fontSize: 12.5,
  lineHeight: 1.5,
  color: T.color.body,
  margin: '10px 0 0',
  maxWidth: '70ch',
}

/**
 * A number the program manager types, held as text while they type it.
 *
 * Binding a `type="number"` straight to a number in state is the standard way
 * to make `0.21` untypable: the moment the box reads `0.`, `Number('0.')` is
 * `0`, a re-render writes `0` back into the box, and the next keystroke lands
 * after it. So the text is this field's own state and the document only ever
 * receives a finite parse of it.
 *
 * The resync guard is the other half. The box must follow the document when the
 * document moves for some *other* reason — a draft loading, a discard — and
 * must not follow it when the document moved because of this very keystroke.
 * `emitted` remembers what this field last sent, so "the value changed and it
 * was not me" is a question it can answer. Without it, clearing the box
 * (`value` unchanged, `text` now empty) immediately refills it.
 */
function NumberField(props: {
  label: string
  value: number
  step?: string
  onChange: (value: number) => void
  hint?: string
  /**
   * True inside the factor table, where the column header already says what the
   * cell is and a repeated visible label per row would be noise. The name
   * still reaches the accessibility tree — and `getByLabelText` — through
   * `aria-label`, which is the difference between a quiet label and no label.
   */
  labelHidden?: boolean
}): JSX.Element {
  const { label, value, step, onChange, hint, labelHidden = false } = props
  const id = useId()
  const [text, setText] = useState(() => String(value))
  const emitted = useRef(value)

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value
      setText(String(value))
    }
  }, [value])

  const input = (
    <input
      id={labelHidden ? undefined : id}
      aria-label={labelHidden ? label : undefined}
      type="number"
      step={step ?? 'any'}
      value={text}
      onChange={(event) => {
        const raw = event.target.value
        setText(raw)
        if (raw.trim() === '') return
        const parsed = Number(raw)
        if (!Number.isFinite(parsed)) return
        emitted.current = parsed
        onChange(parsed)
      }}
      style={labelHidden ? { ...field, width: 92 } : field}
    />
  )

  if (labelHidden) return input

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{label}</label>
      {input}
      {hint === undefined ? null : <span style={{ fontSize: 12, color: T.color.micro }}>{hint}</span>}
    </div>
  )
}

function DateField(props: { label: string; value: string; onChange: (value: string) => void }): JSX.Element {
  const id = useId()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{props.label}</label>
      {/*
        `type="date"`, and the season is why. The season ends on 15 October — a
        month picker cannot express that, and a console whose controls cannot
        say what the program's own dates are is a console that will quietly
        round them.
      */}
      <input
        id={id}
        type="date"
        value={props.value}
        onChange={(event) => { props.onChange(event.target.value) }}
        style={field}
      />
    </div>
  )
}

function CheckField(props: { label: string; checked: boolean; onChange: (value: boolean) => void }): JSX.Element {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.color.body }}>
      <input
        type="checkbox"
        checked={props.checked}
        onChange={(event) => { props.onChange(event.target.checked) }}
      />
      {props.label}
    </label>
  )
}

function Section(props: { id: ConsoleSection; title: string; children: ReactNode }): JSX.Element {
  return (
    <section id={`config-${props.id}`} data-testid={`section-${props.id}`} style={sectionCard}>
      <h2 style={{ margin: 0, fontSize: 16, fontWeight: T.weight.bold }}>{props.title}</h2>
      {props.children}
    </section>
  )
}

type Load =
  | { phase: 'loading' }
  /**
   * The 401, which was reaching the `failed` branch and its "Try again".
   *
   * `ApiError.sessionEnded` has existed since `api.ts` was written and had no
   * reader anywhere in `packages/web`: both screens Plan 6 added branched on
   * `status === 403` alone, so an expired session — the single commonest way
   * these two screens fail after a laptop has been shut — rendered a retry
   * prompt for the one condition retrying cannot fix.
   */
  | { phase: 'signedOut' }
  | { phase: 'forbidden' }
  | { phase: 'failed' }
  | { phase: 'ready'; base: ConfigVersion; library: StrategyLibraryInForce }

type Preview =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'failed'; message: string }
  | { phase: 'ready'; preview: BlastRadiusPreview; forSignature: string }

export interface ConfigConsoleProps {
  /**
   * Which surface to open on. Every surface is rendered either way — this only
   * decides which one the page is scrolled to and which nav entry is marked.
   */
  initialTab?: ConsoleSection
}

export function ConfigConsole({ initialTab = 'season' }: ConfigConsoleProps): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [draft, setDraft] = useState<ConfigDocument | null>(null)
  const [draftErrors, setDraftErrors] = useState<string[]>([])
  const [preview, setPreview] = useState<Preview>({ phase: 'idle' })
  const [notice, setNotice] = useState<string | null>(null)
  const [problem, setProblem] = useState<string[] | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [committed, setCommitted] = useState<{ label: string; effectiveFrom: string } | null>(null)
  const [busy, setBusy] = useState(false)

  const [asOf] = useState(today)
  const [label, setLabel] = useState(() => `Configuration ${today()}`)
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [notes, setNotes] = useState('')

  /**
   * The strategy library's own draft and its own commit state.
   *
   * Held separately from `draft` for the reason the section says out loud: it
   * is a different document in a different table behind a different route, and
   * one piece of state covering both would be the screen quietly implying that
   * one button commits them together.
   */
  const [library, setLibrary] = useState<StrategyLibrary | null>(null)
  const [libraryEffectiveFrom, setLibraryEffectiveFrom] = useState('')
  const [libraryConfirming, setLibraryConfirming] = useState(false)
  const [libraryCommitted, setLibraryCommitted] = useState<string | null>(null)
  const [libraryProblem, setLibraryProblem] = useState<string | null>(null)
  const [libraryBusy, setLibraryBusy] = useState(false)

  const labelId = useId()
  const effectiveId = useId()
  const notesId = useId()
  const libraryEffectiveId = useId()

  const alive = useRef(true)
  const era = useRef(0)

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    let cancelled = false
    /*
      Three requests, and all three are needed. `GET /api/config/at` is the
      document in force — the thing an edit is an edit *of*, and the only honest
      starting point, because a client-side default would be a second copy of
      `defaults.ts` that drifts. `GET /api/config/draft` is this program
      manager's own work in progress, which is the entire reason drafts exist:
      a half-finished rate table has to survive a closed laptop.
      `GET /api/strategies` is the sixth surface's document, which lives in its
      own table and cannot come down with the other two.

      In one `Promise.all` rather than a fetch of its own, so that "the console
      has loaded" stays a single fact. A second phase would mean a strategy
      control that exists a beat after the rate control does — and a test
      awaiting one while querying the other, which is the defect this screen's
      own `openConsole` helper was written to kill.

      The library route is `requireAuth`, not `requireRole`, so it cannot be the
      403 that turns this screen into the forbidden phase; only the two config
      routes can. That asymmetry is why adding it here does not muddy the branch
      below.
    */
    void Promise.all([api.configAt(asOf), api.configDraft(), api.strategyLibrary()])
      .then(([base, saved, inForce]) => {
        if (cancelled) return
        setLoad({ phase: 'ready', base, library: inForce })
        setDraft(saved.document ?? base.document)
        setDraftErrors(saved.errors)
        setLibrary(inForce.library)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Neither 401 nor 403 is a failure to report as one, and they are not
        // the same refusal. A 401 is an expired session and the answer is to
        // sign in again; a 403 is `requireRole('program_manager')` and the
        // answer is that this screen is not yours. "Try again" is wrong for
        // both, and it was what a 401 got.
        //
        // `sessionEnded` rather than `status === 401`, because `api.ts` already
        // owns that mapping and a second copy of it here is how the two come
        // apart.
        if (error instanceof ApiError && error.sessionEnded) setLoad({ phase: 'signedOut' })
        else if (error instanceof ApiError && error.status === 403) setLoad({ phase: 'forbidden' })
        else setLoad({ phase: 'failed' })
      })
    return () => { cancelled = true }
  }, [asOf])

  const signature = draft === null ? '' : JSON.stringify(draft)
  const baseSignature = load.phase === 'ready' ? JSON.stringify(load.base.document) : ''
  const dirty = signature !== '' && signature !== baseSignature

  /**
   * The preview describes the document on screen, or it is discarded.
   *
   * Not dimmed, not badged "stale" — removed, along with the commit control it
   * gates. A panel of numbers that no longer describes the draft beside it is
   * worse than no panel: it is the specific failure ("the screen said 11
   * accounts move") that this whole surface exists to prevent.
   */
  useEffect(() => {
    if (preview.phase === 'ready' && preview.forSignature !== signature) {
      setPreview({ phase: 'idle' })
      setConfirming(false)
    }
  }, [preview, signature])

  /**
   * Which surface the reader is on, and it is a live fact rather than a prop.
   *
   * The nav's underline was drawn on `section.id === initialTab`. `App.tsx`
   * renders `<ConfigConsole />` with no props on every path that reaches this
   * screen, so `initialTab` was always its `'season'` default: the mark sat
   * permanently under "Season & windows" whatever the reader clicked, and the
   * `scrollIntoView` below always targeted the section already at the top of
   * the page. Both were dead.
   *
   * The fragment is the fix rather than a new prop, because the nav entries are
   * already `<a href="#config-…">`: the browser scrolls, the back button works,
   * and a deep link to `/config#config-rates` opens on rates and marks it. The
   * click handler sets the same state directly rather than waiting for
   * `hashchange`, so the mark moves on the click that caused it; the listener
   * is what catches back, forward and a pasted URL.
   *
   * `initialTab` survives as the explicit override — Task 7a passes
   * `strategies` — and as the answer when there is no fragment at all.
   */
  const [current, setCurrent] = useState<ConsoleSection>(
    () => sectionFromHash(window.location.hash) ?? initialTab,
  )
  useEffect(() => {
    const onHash = (): void => {
      const named = sectionFromHash(window.location.hash)
      if (named !== null) setCurrent(named)
    }
    window.addEventListener('hashchange', onHash)
    return () => { window.removeEventListener('hashchange', onHash) }
  }, [])

  /**
   * Opening on a surface. Every surface is rendered either way, so this scrolls
   * rather than swaps — which is what keeps it from being the decorative
   * control this codebase keeps refusing to ship. jsdom implements no
   * `scrollIntoView`, so the optional call is a no-op under test instead of a
   * throw.
   *
   * Once, on the load that first has something to scroll to, and against the
   * section resolved at mount rather than the one the reader has since clicked
   * — a later click is the browser's own fragment navigation and does not want
   * a second scroll on top of it.
   */
  const opened = useRef(false)
  const openOn = useRef(current)
  useEffect(() => {
    if (load.phase !== 'ready' || opened.current) return
    opened.current = true
    window.document.getElementById(`config-${openOn.current}`)?.scrollIntoView?.({ block: 'start' })
  }, [load.phase])

  const edit = useCallback((next: ConfigDocument) => {
    setDraft(next)
    setNotice(null)
    setProblem(null)
    setCommitted(null)
  }, [])

  const review = (): void => {
    if (draft === null) return
    const mine = era.current + 1
    era.current = mine
    const forSignature = JSON.stringify(draft)
    setPreview({ phase: 'running' })
    setProblem(null)
    void api.configBlastRadius({ document: draft, asOfDate: asOf, limit: PREVIEW_LIMIT })
      .then((result) => {
        if (!alive.current || era.current !== mine) return
        setPreview({ phase: 'ready', preview: result, forSignature })
      })
      .catch((error: unknown) => {
        if (!alive.current || era.current !== mine) return
        if (error instanceof ApiError && error.status === 400) {
          setPreview({ phase: 'failed', message: PREVIEW_FAILED })
          // Same route, same `{ error, errors }` body as the commit: this
          // refusal is `validateConfig`'s list, so it is shown as a list.
          setProblem(refusalReasons(error, 'This draft is not a valid configuration.'))
          return
        }
        setPreview({ phase: 'failed', message: PREVIEW_FAILED })
      })
  }

  const saveDraft = (): void => {
    if (draft === null) return
    setBusy(true)
    void api.saveConfigDraft(draft)
      .then((view) => {
        if (!alive.current) return
        setDraftErrors(view.errors)
        setNotice(DRAFT_SAVED)
      })
      .catch(() => { if (alive.current) setProblem([DRAFT_SAVE_FAILED]) })
      .finally(() => { if (alive.current) setBusy(false) })
  }

  const discard = (): void => {
    if (load.phase !== 'ready') return
    const inForce = load.base.document
    setBusy(true)
    // The saved draft is replaced with the document in force rather than left
    // behind, so a reload does not resurrect the edit that was just discarded.
    void api.saveConfigDraft(inForce)
      .then((view) => {
        if (!alive.current) return
        setDraft(inForce)
        setDraftErrors(view.errors)
        setPreview({ phase: 'idle' })
        setConfirming(false)
        setNotice(null)
        setProblem(null)
      })
      .catch(() => { if (alive.current) setProblem([DRAFT_SAVE_FAILED]) })
      .finally(() => { if (alive.current) setBusy(false) })
  }

  const commit = (): void => {
    if (draft === null) return
    setBusy(true)
    setProblem(null)
    void api.commitConfig({
      document: draft,
      label,
      effectiveFrom,
      // Assigned rather than spelled `notes: undefined`: under
      // `exactOptionalPropertyTypes` an explicit undefined is not an absent key,
      // and the route refuses a `notes` that is not a string.
      ...(notes.trim() === '' ? {} : { notes }),
    })
      .then((version) => {
        if (!alive.current) return
        setCommitted({ label: version.label, effectiveFrom: version.effectiveFrom })
        setConfirming(false)
        setPreview({ phase: 'idle' })
      })
      .catch((error: unknown) => {
        if (!alive.current) return
        setConfirming(false)
        setProblem(refusalReasons(error, COMMIT_FAILED))
      })
      .finally(() => { if (alive.current) setBusy(false) })
  }

  /**
   * The library's commit, and the second reason this screen has two of
   * everything: it is `POST /api/strategies`, not `POST /api/config/commit`,
   * and it carries an **envelope** — `{ document, effectiveFrom }` — rather
   * than the bare document.
   *
   * Nothing is validated here first. `validateStrategyLibrary` is the engine's
   * and this package does not depend on it; a browser-side second opinion would
   * be a second answer to a question the store already answers (R-17). A 400
   * comes back carrying the server's own list of reasons and that list is what
   * is shown.
   */
  const commitLibrary = (): void => {
    if (library === null) return
    setLibraryBusy(true)
    setLibraryProblem(null)
    void api.commitStrategyLibrary({ document: library, effectiveFrom: libraryEffectiveFrom })
      .then((committedVersion) => {
        if (!alive.current) return
        setLibraryCommitted(committedVersion.id)
        setLibraryConfirming(false)
      })
      .catch((error: unknown) => {
        if (!alive.current) return
        setLibraryConfirming(false)
        setLibraryProblem(
          error instanceof ApiError ? error.detail ?? error.message : LIBRARY_COMMIT_FAILED,
        )
      })
      .finally(() => { if (alive.current) setLibraryBusy(false) })
  }

  if (load.phase === 'loading') {
    return (
      <div data-testid="config-console" data-phase="loading" style={{ padding: '24px' }}>
        <p style={{ fontSize: 13.5, color: T.color.micro, margin: 0 }}>Loading the configuration…</p>
      </div>
    )
  }
  if (load.phase === 'signedOut') {
    return (
      <div data-testid="config-console" data-phase="signed-out" style={{ padding: '24px' }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {SESSION_ENDED}
        </p>
      </div>
    )
  }
  if (load.phase === 'forbidden') {
    return (
      <div data-testid="config-console" data-phase="forbidden" style={{ padding: '24px' }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {CONFIG_FORBIDDEN}
        </p>
      </div>
    )
  }
  if (load.phase === 'failed' || draft === null) {
    return (
      <div data-testid="config-console" data-phase="failed" style={{ padding: '24px' }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {CONFIG_LOAD_FAILED}
        </p>
      </div>
    )
  }

  const doc = draft
  const base = load.base
  const inForceLibrary = load.library
  const previewCurrent = preview.phase === 'ready' && preview.forSignature === signature

  return (
    <div
      data-testid="config-console"
      data-phase="ready"
      data-dirty={dirty ? 'true' : 'false'}
      style={{ display: 'flex', flexDirection: 'column' }}
    >
      <div
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '18px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 style={{ fontSize: 23, fontWeight: T.weight.bold, letterSpacing: '-0.01em', margin: 0 }}>
            {CONFIG_HEADING}
          </h1>
          <div data-testid="config-provenance" style={{ fontSize: 12.5, color: T.color.muted, marginTop: 5 }}>
            {`In force on ${formatDate(asOf)}: ${base.label} · effective ${formatDate(base.effectiveFrom)}`}
            {dirty ? ' · editing a draft' : ''}
          </div>
        </div>
        <TimeTravel today={asOf} />
      </div>

      <nav
        aria-label="Configuration surfaces"
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '0 24px',
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
        }}
      >
        {SECTIONS.map((section) => (
          <a
            key={section.id}
            href={`#config-${section.id}`}
            /*
              `data-current`, not `aria-current`. Every surface is on this one
              page, so none of them is "the current page" and claiming one is
              would put a second `aria-current` on a screen whose app bar
              already carries the real one.
            */
            data-current={section.id === current ? 'true' : undefined}
            onClick={() => { setCurrent(section.id) }}
            style={{
              padding: '14px 12px',
              fontSize: 11,
              fontWeight: T.weight.semi,
              letterSpacing: '0.09em',
              textTransform: T.micro.transform,
              color: T.color.ink,
              textDecoration: 'none',
              borderBottom: `3px solid ${section.id === current ? T.color.red : 'transparent'}`,
            }}
          >
            {section.label}
          </a>
        ))}
      </nav>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 340px', alignItems: 'start' }}>
        {/*
          One form, no submit. Enter inside a text box submits the nearest form,
          and a reload here would throw away an unsaved draft; every button in
          this screen is `type="button"` for the same reason, so that no
          keystroke can reach the commit.
        */}
        <form
          onSubmit={(event) => { event.preventDefault() }}
          style={{ padding: '18px 24px', minWidth: 0 }}
        >
          <Section id="season" title="Season and event windows">
            <p style={help}>
              Arbitrary dates, not months. The season ends on 15 October, which no month picker can say.
            </p>
            <div style={gridRow}>
              <DateField
                label="Season start"
                value={doc.season.start}
                onChange={(value) => { edit({ ...doc, season: { ...doc.season, start: value } }) }}
              />
              <DateField
                label="Season end"
                value={doc.season.end}
                onChange={(value) => { edit({ ...doc, season: { ...doc.season, end: value } }) }}
              />
              <NumberField
                label="Event window start hour"
                value={doc.eventWindow.startHour}
                step="1"
                hint="24-hour clock, local time"
                onChange={(value) => {
                  edit({ ...doc, eventWindow: { ...doc.eventWindow, startHour: value } })
                }}
              />
              <NumberField
                label="Event window end hour"
                value={doc.eventWindow.endHour}
                step="1"
                onChange={(value) => {
                  edit({ ...doc, eventWindow: { ...doc.eventWindow, endHour: value } })
                }}
              />
              <NumberField
                label="Assumed events per season"
                value={doc.events.assumedCount}
                step="1"
                onChange={(value) => { edit({ ...doc, events: { ...doc.events, assumedCount: value } }) }}
              />
              <NumberField
                label="Assumed event duration"
                value={doc.events.assumedDurationHours}
                hint="hours"
                onChange={(value) => {
                  edit({ ...doc, events: { ...doc.events, assumedDurationHours: value } })
                }}
              />
            </div>
          </Section>

          <Section id="rates" title="Rates and payment terms">
            <div style={gridRow}>
              <NumberField
                label="Energy rate"
                value={doc.rates.energyPerKwh}
                hint="$ per kWh"
                onChange={(value) => { edit({ ...doc, rates: { ...doc.rates, energyPerKwh: value } }) }}
              />
              <NumberField
                label="Demand rate"
                value={doc.rates.demandPerKwMonth}
                hint="$ per kW-month"
                onChange={(value) => { edit({ ...doc, rates: { ...doc.rates, demandPerKwMonth: value } }) }}
              />
              <NumberField
                label="Demand months"
                value={doc.rates.demandMonths}
                hint="months of demand billing in the season"
                onChange={(value) => { edit({ ...doc, rates: { ...doc.rates, demandMonths: value } }) }}
              />
              <NumberField
                label="Nomination floor"
                value={doc.rates.nominationFloorKw}
                hint="kW"
                onChange={(value) => { edit({ ...doc, rates: { ...doc.rates, nominationFloorKw: value } }) }}
              />
              <NumberField
                label="Nomination ceiling"
                value={doc.rates.nominationCeilingKw}
                hint="kW"
                onChange={(value) => {
                  edit({ ...doc, rates: { ...doc.rates, nominationCeilingKw: value } })
                }}
              />
            </div>
            {doc.rates.shortfall === null
              ? <p style={help}>This program has no shortfall terms.</p>
              : (
                <div style={gridRow}>
                  <NumberField
                    label="Shortfall penalty"
                    value={doc.rates.shortfall.penaltyPerKw}
                    hint="$ per kW of shortfall"
                    onChange={(value) => {
                      const shortfall = doc.rates.shortfall
                      if (shortfall === null) return
                      edit({ ...doc, rates: { ...doc.rates, shortfall: { ...shortfall, penaltyPerKw: value } } })
                    }}
                  />
                  <NumberField
                    label="Shortfall tolerance"
                    value={doc.rates.shortfall.toleranceFraction}
                    hint="fraction of the nomination forgiven"
                    onChange={(value) => {
                      const shortfall = doc.rates.shortfall
                      if (shortfall === null) return
                      edit({
                        ...doc,
                        rates: { ...doc.rates, shortfall: { ...shortfall, toleranceFraction: value } },
                      })
                    }}
                  />
                </div>
              )}
          </Section>

          <Section id="baseline" title="Baseline methodology">
            <div style={gridRow}>
              <BaselineMethod
                value={doc.baseline.method}
                onChange={(value) => { edit({ ...doc, baseline: { ...doc.baseline, method: value } }) }}
              />
              <NumberField
                label="Lookback days"
                value={doc.baseline.lookbackDays}
                step="1"
                hint="how far back comparable days are drawn from"
                onChange={(value) => { edit({ ...doc, baseline: { ...doc.baseline, lookbackDays: value } }) }}
              />
            </div>
            <div style={{ display: 'flex', gap: 20, marginTop: 14, flexWrap: 'wrap' }}>
              <CheckField
                label="Exclude weekends from comparable days"
                checked={doc.baseline.excludeWeekends}
                onChange={(value) => {
                  edit({ ...doc, baseline: { ...doc.baseline, excludeWeekends: value } })
                }}
              />
              <CheckField
                label="Exclude prior event days"
                checked={doc.baseline.excludePriorEventDays}
                onChange={(value) => {
                  edit({ ...doc, baseline: { ...doc.baseline, excludePriorEventDays: value } })
                }}
              />
            </div>

            {/*
              R-13's honesty clause, said where the control is rather than in the
              preview alone. These three fields are real, versioned configuration
              and a settlement path would use them — but this pilot computes
              forward-looking targeting estimates whose event day has not
              happened, so moving them to their extremes changes nothing for any
              account (measured, `config/blast-radius.ts`). A program manager who
              tightened a cap and saw a blast radius of zero would reasonably
              conclude the preview was broken. It is not; the cap cannot bind
              yet, and that is a fact about the pilot rather than about the cap.
            */}
            <div
              data-testid="day-of-adjustment"
              style={{
                marginTop: 16,
                border: `1px solid ${T.color.border}`,
                background: T.tint.stale,
                padding: '12px 14px',
              }}
            >
              <div style={{ ...microLabel, color: T.color.warnInk }}>Day-of adjustment</div>
              <p style={{ ...help, color: T.color.warnInk, margin: '7px 0 0' }}>
                These three fields change no estimate this pilot produces. The day-of adjustment is a
                settlement-time correction against a called event’s actual pre-window load, and every
                estimate here is forward-looking for an event day that has not happened. They are
                versioned like everything else and a settlement path would use them, so a blast radius
                of zero for a change here means the preview cannot see it — not that nothing would move.
              </p>
              <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
                <CheckField
                  label="Day-of adjustment enabled"
                  checked={doc.baseline.dayOfAdjustment.enabled}
                  onChange={(value) => {
                    edit({
                      ...doc,
                      baseline: {
                        ...doc.baseline,
                        dayOfAdjustment: { ...doc.baseline.dayOfAdjustment, enabled: value },
                      },
                    })
                  }}
                />
              </div>
              <div style={gridRow}>
                <NumberField
                  label="Day-of adjustment cap"
                  value={doc.baseline.dayOfAdjustment.capFraction}
                  hint="fraction the baseline may move"
                  onChange={(value) => {
                    edit({
                      ...doc,
                      baseline: {
                        ...doc.baseline,
                        dayOfAdjustment: { ...doc.baseline.dayOfAdjustment, capFraction: value },
                      },
                    })
                  }}
                />
                <NumberField
                  label="Day-of adjustment window"
                  value={doc.baseline.dayOfAdjustment.windowHoursBefore}
                  hint="hours before the event"
                  onChange={(value) => {
                    edit({
                      ...doc,
                      baseline: {
                        ...doc.baseline,
                        dayOfAdjustment: { ...doc.baseline.dayOfAdjustment, windowHoursBefore: value },
                      },
                    })
                  }}
                />
              </div>
            </div>
          </Section>

          <Section id="factors" title="Curtailment factors">
            <FactorTable
              rows={doc.factors}
              buildingTypes={doc.buildingTypes}
              onChange={(factors) => { edit({ ...doc, factors }) }}
            />
          </Section>

          <Section id="buildingTypes" title="Building types and occupancy calendars">
            <p style={help}>
              A calendar says which date ranges a building type is unoccupied — this is how a school
              estimates differently in July than in late September.
            </p>
            <BuildingTypeList
              types={doc.buildingTypes}
              onChange={(buildingTypes) => { edit({ ...doc, buildingTypes }) }}
            />
          </Section>

          <Section id="strategies" title="Curtailment strategy library and ranking">
            <p style={help}>
              Two documents, two commits. The three weights and the cut-off below are part of this
              configuration draft and go out through <b>Review changes</b> on the right. The library
              itself is a separate effective-dated document with its own commit, further down.
            </p>

            <RankingNotMeasured />

            <div style={{ ...microLabel, marginTop: 18 }}>Ranking weights</div>
            <p style={help}>
              Relative weights, not shares — they need not sum to anything. The shipped program
              favours low disruption over raw yield, which is why <b>impact</b> outweighs the other
              two. Each strategy is scored against the others for this account and the list is
              ordered on the total.
            </p>
            <div style={gridRow}>
              <NumberField
                label="Yield weight"
                value={doc.strategyWeights.yield}
                hint="how much the account-specific kW matters"
                onChange={(value) => {
                  edit({ ...doc, strategyWeights: { ...doc.strategyWeights, yield: value } })
                }}
              />
              <NumberField
                label="Impact weight"
                value={doc.strategyWeights.impact}
                hint="disruption to the customer; low impact scores higher"
                onChange={(value) => {
                  edit({ ...doc, strategyWeights: { ...doc.strategyWeights, impact: value } })
                }}
              />
              <NumberField
                label="Feasibility weight"
                value={doc.strategyWeights.feasibility}
                hint="how achievable the strategy is"
                onChange={(value) => {
                  edit({ ...doc, strategyWeights: { ...doc.strategyWeights, feasibility: value } })
                }}
              />
            </div>

            <div data-testid="negligible-share" style={{ marginTop: 18 }}>
              {/*
                One column, capped. `gridRow`'s `auto-fit` stretches a lone
                child across the whole form, and a full-width box holding `0.05`
                reads as a much bigger quantity than a share.
              */}
              <div style={{ ...gridRow, gridTemplateColumns: 'minmax(0, 320px)' }}>
                <NumberField
                  label={NEGLIGIBLE_SHARE_LABEL}
                  value={doc.negligibleShareThreshold}
                  hint="a share between 0 and 1; 0.05 is five per cent"
                  onChange={(value) => { edit({ ...doc, negligibleShareThreshold: value }) }}
                />
              </div>
              <p style={help}>
                Below this share, a strategy that addresses only that end use is still shown, with
                the reason, and is not quantified. The reason names the account’s own computed
                share, not this number.
              </p>
              <p data-testid="negligible-share-reach" style={{ ...help, color: T.color.warnInk }}>
                {NEGLIGIBLE_SHARE_REACH}
              </p>
            </div>

            {library === null
              ? null
              : (
                <StrategyLibraryEditor
                  library={library}
                  inForceId={inForceLibrary.id}
                  onChange={(next) => {
                    setLibrary(next)
                    setLibraryConfirming(false)
                    setLibraryCommitted(null)
                    setLibraryProblem(null)
                  }}
                  dirty={JSON.stringify(library) !== JSON.stringify(inForceLibrary.library)}
                  effectiveFrom={libraryEffectiveFrom}
                  effectiveId={libraryEffectiveId}
                  onEffectiveFrom={setLibraryEffectiveFrom}
                  confirming={libraryConfirming}
                  onConfirming={setLibraryConfirming}
                  onCommit={commitLibrary}
                  busy={libraryBusy}
                  committedId={libraryCommitted}
                  problem={libraryProblem}
                />
              )}
          </Section>
        </form>

        <aside
          data-testid="commit-rail"
          style={{
            position: 'sticky',
            top: 0,
            padding: '18px 24px 18px 0',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          {draftErrors.length === 0
            ? null
            : (
              <div
                data-testid="draft-errors"
                role="alert"
                style={{ border: `1.5px solid ${T.color.red}`, background: T.tint.red, padding: '12px 14px' }}
              >
                <div style={{ ...microLabel, color: T.color.declinedInk }}>This draft cannot be committed</div>
                <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.declinedInk }}>
                  {draftErrors.map((message) => <li key={message}>{message}</li>)}
                </ul>
              </div>
            )}

          {problem === null
            ? null
            : (
              <div
                data-testid="commit-problem"
                role="alert"
                style={{ border: `1.5px solid ${T.color.red}`, background: T.tint.red, padding: '12px 14px' }}
              >
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: T.color.declinedInk }}>
                  {problem.map((message) => <li key={message}>{message}</li>)}
                </ul>
              </div>
            )}

          {committed === null
            ? null
            : (
              <div
                data-testid="commit-done"
                style={{ border: `1.5px solid ${T.color.green}`, background: T.tint.green, padding: '12px 14px' }}
              >
                <div style={{ ...microLabel, color: T.color.money }}>Committed</div>
                <p style={{ ...help, margin: '6px 0 0' }}>
                  {`${committed.label} takes effect on ${formatDate(committed.effectiveFrom)}. `
                    + 'Nothing was overwritten — every earlier version still reproduces its own numbers.'}
                </p>
              </div>
            )}

          <div
            style={{
              border: `1.5px solid ${T.color.rule}`,
              background: '#fff',
              padding: '14px 16px',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div style={microLabel}>Before you save</div>
            <p style={{ ...help, margin: 0 }}>
              {dirty
                ? `Reviewing scores ${PREVIEW_LIMIT} accounts under this draft and under the `
                  + 'configuration in force, and reports every difference. It is the only way to a commit.'
                : NOTHING_TO_REVIEW}
            </p>
            <button
              type="button"
              onClick={review}
              disabled={!dirty || preview.phase === 'running'}
              style={{
                ...primaryButton,
                ...(dirty && preview.phase !== 'running'
                  ? {}
                  : {
                      background: T.color.chipOff,
                      border: `1.5px solid ${T.color.chipOff}`,
                      cursor: 'not-allowed',
                    }),
              }}
            >
              Review changes
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" onClick={saveDraft} disabled={busy} style={{ ...secondaryButton, flex: 1 }}>
                Save draft
              </button>
              <button
                type="button"
                onClick={discard}
                disabled={busy || !dirty}
                style={{ ...secondaryButton, flex: 1 }}
              >
                Discard changes
              </button>
            </div>
            {notice === null
              ? null
              : <p data-testid="draft-notice" style={{ ...help, margin: 0, color: T.color.money }}>{notice}</p>}
          </div>

          {preview.phase === 'running'
            ? (
              <p
                data-testid="preview-running"
                aria-live="polite"
                style={{ ...help, margin: 0, color: T.color.micro }}
              >
                {PREVIEW_RUNNING}
              </p>
            )
            : null}

          {preview.phase === 'failed'
            ? (
              <p role="alert" data-testid="preview-failed" style={{ ...help, margin: 0, color: T.color.warnInk }}>
                {preview.message}
              </p>
            )
            : null}

          {previewCurrent && preview.phase === 'ready'
            ? (
              <>
                <BlastRadius radius={preview.preview.radius} diff={preview.preview.diff} />

                <div
                  style={{
                    border: `1.5px solid ${T.color.rule}`,
                    background: '#fff',
                    padding: '14px 16px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                  }}
                >
                  <div style={microLabel}>Commit this configuration</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label htmlFor={labelId} style={microLabel}>Version label</label>
                    <input
                      id={labelId}
                      type="text"
                      value={label}
                      onChange={(event) => { setLabel(event.target.value) }}
                      style={field}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label htmlFor={effectiveId} style={microLabel}>Effective from</label>
                    {/*
                      Empty, always, and there is no default. A commit is
                      effective-dated and the date decides which estimates the
                      new document explains; a pre-filled "today" is a date
                      nobody chose, arriving on a screen whose whole subject is
                      that versions are deliberate.
                    */}
                    <input
                      id={effectiveId}
                      type="date"
                      value={effectiveFrom}
                      onChange={(event) => { setEffectiveFrom(event.target.value) }}
                      style={field}
                    />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label htmlFor={notesId} style={microLabel}>Notes (optional)</label>
                    <textarea
                      id={notesId}
                      value={notes}
                      rows={2}
                      onChange={(event) => { setNotes(event.target.value) }}
                      style={{ ...field, fontFamily: T.fontFamily, resize: 'vertical' }}
                    />
                  </div>

                  {confirming
                    ? (
                      /*
                        The second act, and the reason there is one: this
                        commits a document that moves every estimate in the
                        book. The figure is restated here, beside the button,
                        rather than left a scroll away — and the blast radius
                        itself is directly above, still on screen, because this
                        block is inside the same rail rather than in an overlay
                        that would cover it.
                      */
                      <div
                        data-testid="commit-confirm"
                        style={{ border: `1.5px solid ${T.color.red}`, background: T.tint.red, padding: '12px 14px' }}
                      >
                        <p style={{ ...help, margin: 0, color: T.color.declinedInk }}>
                          {`This creates a new version effective ${formatDate(effectiveFrom)}. `
                            + `${preview.preview.radius.accountsMoved} of `
                            + `${preview.preview.radius.accountsConsidered} scored accounts move`
                            + (preview.preview.radius.sampled === null
                              ? ', which is every in-scope account.'
                              : `, and that is a sample of ${preview.preview.radius.sampled.of}.`)}
                        </p>
                        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                          <button type="button" onClick={commit} disabled={busy} style={dangerButton}>
                            Yes, commit this version
                          </button>
                          <button
                            type="button"
                            onClick={() => { setConfirming(false) }}
                            style={secondaryButton}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )
                    : (
                      <button
                        type="button"
                        onClick={() => { setConfirming(true) }}
                        disabled={effectiveFrom === '' || label.trim() === '' || busy}
                        style={{
                          ...primaryButton,
                          ...(effectiveFrom === '' || label.trim() === '' || busy
                            ? {
                                background: T.color.chipOff,
                                border: `1.5px solid ${T.color.chipOff}`,
                                cursor: 'not-allowed',
                              }
                            : {}),
                        }}
                      >
                        Commit configuration
                      </button>
                    )}
                  <p style={{ ...help, margin: 0, color: T.color.micro }}>
                    Nothing is overwritten. Committing creates a new version with an effective date; the
                    version in force still reproduces today’s numbers.
                  </p>
                </div>
              </>
            )
            : null}
        </aside>
      </div>
    </div>
  )
}

function BaselineMethod(props: {
  value: ConfigDocument['baseline']['method']
  onChange: (value: ConfigDocument['baseline']['method']) => void
}): JSX.Element {
  const id = useId()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>Comparable-day selection</label>
      <select
        id={id}
        value={props.value}
        onChange={(event) => { props.onChange(event.target.value as ConfigDocument['baseline']['method']) }}
        style={field}
      >
        <option value="10in10">Highest 10 of the last 10 comparable days</option>
        <option value="5in10">Highest 5 of the last 10 comparable days</option>
        <option value="high4of5">Highest 4 of the last 5 comparable days</option>
      </select>
    </div>
  )
}

/**
 * The two-dimensional factor table, keyed on industry code and building type.
 *
 * **The industry-only row is not a fallback to tidy away.** A row with no
 * building type is how an uncharacterized account is scored at all (display
 * rule 4, R-23), so it is labelled as a deliberate row rather than drawn as a
 * blank cell that reads like missing data — and the sentence under the table
 * says why, which is the wireframe's own footnote.
 *
 * The wireframe carries an `Accounts` column counting how many accounts each
 * row covers. **No route serves that figure**, and a column of numbers derived
 * in the browser from nothing would be the one thing on this screen a program
 * manager could not check. It is left out rather than invented.
 *
 * 1f's columns are `Cooling / Base / Schedule`, which are load *components*.
 * The document is keyed on **end use** — `factors` is `(naics, buildingType,
 * endUse) -> shed, shift` — and the component-to-end-use mapping is `endUseMix`,
 * a separate table. The wireframe was drawn before that split; the table below
 * follows the document, because a console that edits a shape the storage does
 * not have edits nothing.
 */
function FactorTable(props: {
  rows: ConfigFactorRow[]
  buildingTypes: ConfigBuildingType[]
  onChange: (rows: ConfigFactorRow[]) => void
}): JSX.Element {
  const { rows, buildingTypes, onChange } = props

  /**
   * Three kinds of row, and the middle one is the one the wireframe highlights.
   *
   * `naics: null, buildingType: null` is the **universal** last resort — the row
   * that scores an account whose industry has no row of its own. Calling that
   * one an "industry-only row" is what the first draft of this table did, and
   * driving it against the seeded book printed the cell pair
   * *"Any industry / Industry-only row"*, which names an industry that is not
   * there. The distinction is not cosmetic: a program manager editing the
   * universal cooling row is editing the row that (measured, exhaustively, over
   * all 313 in-scope accounts) binds for **none** of them, and a table that
   * calls it the same thing as `452210`'s industry row hides that.
   */
  const kindOf = (row: ConfigFactorRow): 'universal' | 'industry-only' | 'specific' => {
    if (row.buildingType !== null) return 'specific'
    return row.naics === null ? 'universal' : 'industry-only'
  }

  const labelFor = (row: ConfigFactorRow): string => {
    if (row.buildingType === null) return kindOf(row) === 'universal' ? 'Any building type' : 'Industry-only row'
    return buildingTypes.find((type) => type.id === row.buildingType)?.label ?? asWords(row.buildingType)
  }

  const setAt = (index: number, next: Partial<ConfigFactorRow>): void => {
    onChange(rows.map((row, at) => (at === index ? { ...row, ...next } : row)))
  }

  const cell: CSSProperties = {
    padding: '6px 8px',
    borderTop: `1px solid ${T.color.hairline}`,
    fontSize: 13,
    color: T.color.ink,
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 520 }}>
          <thead>
            <tr>
              {['Industry code', 'Building type', 'End use', 'Shed fraction', 'Shift fraction'].map((head) => (
                <th
                  key={head}
                  style={{ ...cell, ...microLabel, borderTop: 0, textAlign: 'left', whiteSpace: 'nowrap' }}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const kind = kindOf(row)
              const key = `${row.naics ?? '*'}|${row.buildingType ?? '*'}|${row.endUse}`
              return (
                <tr
                  key={key}
                  data-testid="factor-row"
                  data-row-kind={kind}
                  style={kind === 'specific' ? undefined : { background: T.tint.blue }}
                >
                  <td style={cell}>{row.naics ?? 'Any industry'}</td>
                  <td style={{ ...cell, color: kind === 'specific' ? T.color.ink : T.color.muted }}>
                    {labelFor(row)}
                  </td>
                  <td style={cell}>{asWords(row.endUse)}</td>
                  <td style={cell}>
                    <NumberField
                      labelHidden
                      label={`${row.naics ?? 'Any industry'} · ${labelFor(row)} · `
                        + `${asWords(row.endUse)} shed fraction`}
                      value={row.shedFraction}
                      onChange={(value) => { setAt(index, { shedFraction: value }) }}
                    />
                  </td>
                  <td style={cell}>
                    <NumberField
                      labelHidden
                      label={`${row.naics ?? 'Any industry'} · ${labelFor(row)} · `
                        + `${asWords(row.endUse)} shift fraction`}
                      value={row.shiftFraction}
                      onChange={(value) => { setAt(index, { shiftFraction: value }) }}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p data-testid="industry-only-note" style={help}>
        Industry-only rows stay valid on their own — an account with no building type is scored
        from the industry row, not excluded.
      </p>
      <p data-testid="universal-row-note" style={{ ...help, marginTop: 6 }}>
        The rows under <b>Any industry</b> are the last resort, for an account whose industry has no
        row of its own. Every account in the seeded book has one, so editing those rows moves
        nothing — which the blast radius will report as a true zero rather than as a blind spot.
      </p>
    </div>
  )
}

/**
 * Building types and their occupancy calendars (R-23).
 *
 * `occupancyCalendar: null` and `occupancyCalendar: []` are different claims —
 * "occupied normally all season" and "an empty calendar" — so the null case is
 * a sentence rather than an empty list, and adding the first period is an
 * explicit act.
 */
function BuildingTypeList(props: {
  types: ConfigBuildingType[]
  onChange: (types: ConfigBuildingType[]) => void
}): JSX.Element {
  const { types, onChange } = props

  const setAt = (index: number, next: ConfigBuildingType): void => {
    onChange(types.map((type, at) => (at === index ? next : type)))
  }

  return (
    <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {types.map((type, index) => (
        <div
          key={type.id}
          data-testid="building-type"
          data-building-type={type.id}
          style={{ border: `1px solid ${T.color.hairline}`, padding: '12px 14px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <div style={{ fontSize: 13.5, fontWeight: T.weight.semi }}>{type.label}</div>
            <code style={{ fontSize: 12, color: T.color.micro }}>{type.id}</code>
          </div>
          {type.occupancyCalendar === null
            ? (
              <div style={{ marginTop: 8 }}>
                <p style={{ ...help, margin: 0, color: T.color.micro }}>
                  Occupied normally for the whole season. No calendar has been entered.
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setAt(index, { ...type, occupancyCalendar: [] })
                  }}
                  style={{ ...secondaryButton, marginTop: 8 }}
                >
                  {`Add a calendar for ${type.label}`}
                </button>
              </div>
            )
            : (
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {type.occupancyCalendar.map((period, at) => (
                  <div key={`${type.id}-${String(at)}`} style={gridRow}>
                    <DateField
                      label={`${type.label} period ${String(at + 1)} start`}
                      value={period.start}
                      onChange={(value) => {
                        const calendar = type.occupancyCalendar ?? []
                        setAt(index, {
                          ...type,
                          occupancyCalendar: calendar.map(
                            (one, which) => (which === at ? { ...one, start: value } : one),
                          ),
                        })
                      }}
                    />
                    <DateField
                      label={`${type.label} period ${String(at + 1)} end`}
                      value={period.end}
                      onChange={(value) => {
                        const calendar = type.occupancyCalendar ?? []
                        setAt(index, {
                          ...type,
                          occupancyCalendar: calendar.map(
                            (one, which) => (which === at ? { ...one, end: value } : one),
                          ),
                        })
                      }}
                    />
                    <NumberField
                      label={`${type.label} period ${String(at + 1)} occupancy factor`}
                      value={period.occupancyFactor}
                      hint="1.0 is normal operation, 0.1 is effectively unoccupied"
                      onChange={(value) => {
                        const calendar = type.occupancyCalendar ?? []
                        setAt(index, {
                          ...type,
                          occupancyCalendar: calendar.map(
                            (one, which) => (which === at ? { ...one, occupancyFactor: value } : one),
                          ),
                        })
                      }}
                    />
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    const calendar = type.occupancyCalendar ?? []
                    setAt(index, {
                      ...type,
                      occupancyCalendar: [...calendar, { start: '', end: '', occupancyFactor: 1 }],
                    })
                  }}
                  style={{ ...secondaryButton, alignSelf: 'flex-start' }}
                >
                  {`Add a period to ${type.label}`}
                </button>
              </div>
            )}
        </div>
      ))}
    </div>
  )
}

/**
 * The strategy tab's statement about its own blast radius, drawn where the
 * controls are rather than only inside a panel nobody has asked for yet.
 *
 * The `BlastRadius` panel already answers this correctly once a preview runs:
 * the server lists both paths in `CONFIG_PATHS_NOT_EVALUATED`, so `blindTo`
 * carries them and the panel draws *"Not evaluated by this preview"* above the
 * headline with `data-coverage="partial"`. This block is the same fact one step
 * earlier, because a program manager who reads "0 accounts move" for a weights
 * change and closes the screen never sees the panel's version of it.
 *
 * The two paths are named as the dotted paths the preview reports, so a reader
 * can match this sentence to the list in the panel rather than having to trust
 * that they are about the same thing.
 */
function RankingNotMeasured(): JSX.Element {
  return (
    <div
      data-testid="ranking-not-measured"
      style={{
        marginTop: 14,
        border: `1px solid ${T.color.border}`,
        background: T.tint.stale,
        padding: '12px 14px',
      }}
    >
      <div style={{ ...microLabel, color: T.color.warnInk }}>Not measured by the blast radius</div>
      <p style={{ ...help, color: T.color.warnInk, margin: '7px 0 0' }}>{RANKING_NOT_MEASURED}</p>
      <ul style={{ margin: '7px 0 0', paddingLeft: 18, fontSize: 12.5, color: T.color.warnInk }}>
        <li><code>strategyWeights</code></li>
        <li><code>negligibleShareThreshold</code></li>
      </ul>
      <p style={{ ...help, color: T.color.warnInk, margin: '7px 0 0' }}>
        The preview names both of these itself, under <b>Not evaluated by this preview</b>.
      </p>
    </div>
  )
}

const LEVELS: readonly ('low' | 'medium' | 'high')[] = ['low', 'medium', 'high']

/** The seven end uses, in the engine's `END_USES` order, with the words a form uses. */
const END_USE_OPTIONS: readonly { id: EndUse; label: string }[] = [
  { id: 'cooling', label: 'Cooling' },
  { id: 'ventilation', label: 'Ventilation' },
  { id: 'lighting', label: 'Lighting' },
  { id: 'refrigeration', label: 'Refrigeration' },
  { id: 'process', label: 'Process load' },
  { id: 'plug', label: 'Plug load' },
  { id: 'other', label: 'Other load' },
]

function TextField(props: {
  label: string
  value: string
  onChange: (value: string) => void
  hint?: string
}): JSX.Element {
  const id = useId()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{props.label}</label>
      <input
        id={id}
        type="text"
        value={props.value}
        onChange={(event) => { props.onChange(event.target.value) }}
        style={field}
      />
      {props.hint === undefined
        ? null
        : <span style={{ fontSize: 12, color: T.color.micro }}>{props.hint}</span>}
    </div>
  )
}

function TextAreaField(props: {
  label: string
  value: string
  rows: number
  onChange: (value: string) => void
  hint?: string
}): JSX.Element {
  const id = useId()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{props.label}</label>
      <textarea
        id={id}
        rows={props.rows}
        value={props.value}
        onChange={(event) => { props.onChange(event.target.value) }}
        style={{ ...field, fontFamily: T.fontFamily, resize: 'vertical' }}
      />
      {props.hint === undefined
        ? null
        : <span style={{ fontSize: 12, color: T.color.micro }}>{props.hint}</span>}
    </div>
  )
}

function LevelField(props: {
  label: string
  value: 'low' | 'medium' | 'high'
  onChange: (value: 'low' | 'medium' | 'high') => void
}): JSX.Element {
  const id = useId()
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{props.label}</label>
      <select
        id={id}
        value={props.value}
        onChange={(event) => { props.onChange(event.target.value as 'low' | 'medium' | 'high') }}
        style={field}
      >
        {LEVELS.map((level) => <option key={level} value={level}>{level}</option>)}
      </select>
    </div>
  )
}

/**
 * A list of short tokens, comma-separated — NAICS prefixes and building type
 * ids, neither of which can contain a comma.
 *
 * Prerequisites get a textarea instead, one per line: they are sentences and
 * *"Building management system, or a scheduler"* is a prerequisite a
 * comma-separated field would silently split in two.
 *
 * The text is this field's own state for `NumberField`'s reason: a trailing
 * comma is a legal thing to have typed, and re-deriving the string from the
 * parsed array on every keystroke would delete it as fast as it was typed.
 */
function TokenListField(props: {
  label: string
  value: string[]
  onChange: (value: string[]) => void
  hint: string
}): JSX.Element {
  const id = useId()
  const [text, setText] = useState(() => props.value.join(', '))
  const emitted = useRef(props.value)

  useEffect(() => {
    if (props.value !== emitted.current) {
      emitted.current = props.value
      setText(props.value.join(', '))
    }
  }, [props.value])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>{props.label}</label>
      <input
        id={id}
        type="text"
        value={text}
        onChange={(event) => {
          const raw = event.target.value
          setText(raw)
          const parsed = raw.split(',').map((part) => part.trim()).filter((part) => part !== '')
          emitted.current = parsed
          props.onChange(parsed)
        }}
        style={field}
      />
      <span style={{ fontSize: 12, color: T.color.micro }}>{props.hint}</span>
    </div>
  )
}

/**
 * `timing`, in the half-open convention `ConfigDocument.eventWindow` uses —
 * `startHour` inclusive in 0..23, `endHour` exclusive in 1..24.
 *
 * The hour boxes appear only for `hours`, because a start and an end hour beside
 * *"In the window"* are two numbers claiming to mean something they do not. The
 * previous hours are remembered in the strategy object rather than in this
 * component: switching to `in_window` and back is a round trip through
 * `{ kind }`, so the numbers do not survive it, and that is honest — they were
 * not part of the document while the kind was not `hours`.
 */
function TimingField(props: {
  value: Strategy['timing']
  onChange: (value: Strategy['timing']) => void
}): JSX.Element {
  const id = useId()
  const { value, onChange } = props
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label htmlFor={id} style={microLabel}>Timing</label>
      <select
        id={id}
        value={value.kind}
        onChange={(event) => {
          const kind = event.target.value
          if (kind === 'pre_event') onChange({ kind: 'pre_event' })
          else if (kind === 'in_window') onChange({ kind: 'in_window' })
          else onChange({ kind: 'hours', startHour: 15, endHour: 17 })
        }}
        style={field}
      >
        <option value="pre_event">Before the window</option>
        <option value="in_window">In the window</option>
        <option value="hours">Specific hours</option>
      </select>
      {value.kind === 'hours'
        ? (
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <NumberField
              label="Timing start hour"
              value={value.startHour}
              step="1"
              hint="0–23, inclusive"
              onChange={(hour) => { onChange({ ...value, startHour: hour }) }}
            />
            <NumberField
              label="Timing end hour"
              value={value.endHour}
              step="1"
              hint="1–24, exclusive"
              onChange={(hour) => { onChange({ ...value, endHour: hour }) }}
            />
          </div>
        )
        : null}
    </div>
  )
}

/**
 * One strategy, editable.
 *
 * **Every field the document carries is here.** A card that showed
 * `applicableNaicsPrefixes` without letting anyone change it would be the
 * "obviously-missing affordance" this task forbids itself — and that field in
 * particular is the one that decides which accounts see the strategy at all,
 * which makes it the least defensible one to leave read-only.
 *
 * The id is editable too, and it is worth saying why that is safe:
 * `estimates.strategy_library_version_id` stamps which version a rep was shown,
 * and `strategy_presentations` resolves against that stamped version. Versions
 * are immutable, so renaming an id in a *new* version detaches nothing — the
 * old rows still resolve against the old document, which still has the old id.
 */
function StrategyCard(props: {
  strategy: Strategy
  onChange: (strategy: Strategy) => void
  onRemove: () => void
}): JSX.Element {
  const { strategy, onChange, onRemove } = props
  return (
    <div
      data-testid="strategy-card"
      data-strategy-id={strategy.id}
      style={{ border: `1px solid ${T.color.hairline}`, padding: '14px 16px' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <div style={{ fontSize: 13.5, fontWeight: T.weight.semi }}>{strategy.name}</div>
        {/*
          The word is `Remove`; the strategy it removes is on the accessible
          name. Rendering the whole name as the button's visible text — which is
          what the first draft did — printed
          `REMOVE PRE-COOL 2°F BEFORE THE WINDOW` in shouting micro-caps, wider
          than the heading beside it and reading as the loudest thing on a card
          whose subject is the framing statement. Found by looking at it. The
          full name still reaches the accessibility tree, and `getByRole`, which
          is what keeps one row's control distinguishable from another's.
        */}
        <button
          type="button"
          aria-label={`Remove ${strategy.name}`}
          onClick={onRemove}
          style={{ ...secondaryButton, padding: '6px 10px' }}
        >
          Remove
        </button>
      </div>

      <div style={gridRow}>
        <TextField
          label="Strategy id"
          value={strategy.id}
          hint="stable, lower-case, used in the presentation record"
          onChange={(value) => { onChange({ ...strategy, id: value }) }}
        />
        <TextField
          label="Strategy name"
          value={strategy.name}
          onChange={(value) => { onChange({ ...strategy, name: value }) }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <TextAreaField
          label="Framing statement"
          rows={3}
          value={strategy.framingStatement}
          hint="travels with this strategy everywhere it is shown, on screen and in print"
          onChange={(value) => { onChange({ ...strategy, framingStatement: value }) }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={microLabel}>End uses</div>
        <div
          data-testid="strategy-end-uses"
          style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}
        >
          {END_USE_OPTIONS.map((option) => (
            <CheckField
              key={option.id}
              label={option.label}
              checked={strategy.endUses.includes(option.id)}
              onChange={(checked) => {
                onChange({
                  ...strategy,
                  // Filtered out of the canonical order rather than pushed on
                  // the end, so two libraries that address the same end uses
                  // are the same JSON whichever order they were ticked in.
                  endUses: END_USE_OPTIONS
                    .map((each) => each.id)
                    .filter((id) => (id === option.id ? checked : strategy.endUses.includes(id))),
                })
              }}
            />
          ))}
        </div>
      </div>

      <div style={gridRow}>
        <NumberField
          label="Yield fraction"
          value={strategy.yieldFraction}
          hint="of the addressed end uses’ load; greater than 0, at most 1"
          onChange={(value) => { onChange({ ...strategy, yieldFraction: value }) }}
        />
        <LevelField
          label="Impact"
          value={strategy.impact}
          onChange={(value) => { onChange({ ...strategy, impact: value }) }}
        />
        <LevelField
          label="Feasibility"
          value={strategy.feasibility}
          onChange={(value) => { onChange({ ...strategy, feasibility: value }) }}
        />
        <TimingField
          value={strategy.timing}
          onChange={(value) => { onChange({ ...strategy, timing: value }) }}
        />
      </div>

      <div style={{ marginTop: 12 }}>
        <TextAreaField
          label="Prerequisites"
          rows={2}
          value={strategy.prerequisites.join('\n')}
          hint="one per line; stated to the rep as requirements, never used to drop the strategy"
          onChange={(value) => {
            onChange({
              ...strategy,
              prerequisites: value.split('\n').map((line) => line.trim()).filter((line) => line !== ''),
            })
          }}
        />
      </div>

      <div style={gridRow}>
        <TokenListField
          label="Applicable industry codes"
          value={strategy.applicableNaicsPrefixes}
          hint="NAICS prefixes, comma-separated. Empty applies to every industry."
          onChange={(value) => { onChange({ ...strategy, applicableNaicsPrefixes: value }) }}
        />
        <TokenListField
          label="Applicable building types"
          value={strategy.applicableBuildingTypes}
          hint="comma-separated. Empty applies to any, INCLUDING an uncharacterized account."
          onChange={(value) => { onChange({ ...strategy, applicableBuildingTypes: value }) }}
        />
      </div>
    </div>
  )
}

/**
 * The library, its editor and its own commit block (R-31, R-34 in part).
 *
 * ## Why the commit lives here and not in the rail
 *
 * The rail commits a `ConfigDocument` through `POST /api/config/commit`. This
 * commits a strategy library through `POST /api/strategies`, into a different
 * table, with a different effective date. Drawing one button that fires
 * whichever of the two happened to be dirty would be the screen implying they
 * are one act; they are not, and the sentence at the top of the section says so.
 *
 * ## The confirmation is not optional and not one keystroke away
 *
 * A committed library changes the ranked list every rep sees on every account,
 * and — unlike a configuration commit — there are **no numbers in front of it**.
 * So it takes the same two acts the configuration commit takes: an effective
 * date, then `Commit strategy library`, then a confirmation that restates what
 * is about to happen. Every control is `type="button"` inside the console's one
 * form, whose `onSubmit` preventDefaults, so no Enter in any text box reaches
 * any of them.
 *
 * ## An empty library is a state, not an error
 *
 * `resolveStrategyLibraryAt` answers `{ id: null }` with an empty library
 * rather than throwing, which is the state every database is in before the seed
 * runs. `StrategyList.tsx` already refuses to call that "no strategies for this
 * industry"; this screen refuses to call it a failure.
 */
function StrategyLibraryEditor(props: {
  library: StrategyLibrary
  inForceId: string | null
  onChange: (library: StrategyLibrary) => void
  dirty: boolean
  effectiveFrom: string
  effectiveId: string
  onEffectiveFrom: (value: string) => void
  confirming: boolean
  onConfirming: (value: boolean) => void
  onCommit: () => void
  busy: boolean
  committedId: string | null
  problem: string | null
}): JSX.Element {
  const {
    library, inForceId, onChange, dirty, effectiveFrom, effectiveId, onEffectiveFrom,
    confirming, onConfirming, onCommit, busy, committedId, problem,
  } = props

  const setAt = (index: number, strategy: Strategy): void => {
    onChange({ ...library, strategies: library.strategies.map((one, at) => (at === index ? strategy : one)) })
  }

  return (
    <div
      data-testid="strategy-library"
      data-library-version-id={inForceId ?? ''}
      data-dirty={dirty ? 'true' : 'false'}
      style={{ marginTop: 24, borderTop: `1.5px solid ${T.color.rule}`, paddingTop: 16 }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: T.weight.bold }}>The library in force</h3>
        {/*
          The row id, and only the row id. The document's own `version` number
          is NOT the version identity — `resolveStrategyLibraryAt` orders on
          `effective_from, committed_at, rowid` and never reads it, and
          `store.ts` says outright that "no caller should be branching on the
          number anyway". Printing it here as though it identified the version
          would make every commit that left it alone look like the same version
          twice.
        */}
        <code style={{ fontSize: 12, color: T.color.micro }}>
          {inForceId ?? 'no version committed'}
        </code>
      </div>
      <p data-testid="bulk-import-note" style={help}>{BULK_IMPORT_NOT_IN_PILOT}</p>

      {/*
        So the number gets a control instead, for Task 0's reason applied to a
        second field: it is in the document, it round-trips through every
        commit, and a document field no surface can change is worse than a
        constant. `noLibrary()` returns 0 for "before the first", which is a
        convention rather than an enforced sentinel; the hint says so rather
        than the field pretending to a meaning it does not have.
      */}
      <div style={{ ...gridRow, gridTemplateColumns: 'minmax(0, 320px)' }}>
        <NumberField
          label="Library version number"
          value={library.version}
          step="1"
          hint="the program’s own number for this library. Nothing resolves on it."
          onChange={(value) => { onChange({ ...library, version: value }) }}
        />
      </div>

      {library.strategies.length === 0
        ? (
          <p data-testid="library-empty" style={{ ...help, color: T.color.micro }}>
            {NO_STRATEGY_LIBRARY_IN_FORCE}
          </p>
        )
        : (
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 14 }}>
            {library.strategies.map((strategy, index) => (
              <StrategyCard
                /*
                  Index, deliberately. An id is an editable field on this card,
                  so keying on it would remount the card on every keystroke in
                  the id box and take the caret with it. The list only ever
                  changes by an explicit Add or Remove, and every input is
                  controlled from this array.
                */
                key={index}
                strategy={strategy}
                onChange={(next) => { setAt(index, next) }}
                onRemove={() => {
                  onChange({
                    ...library,
                    strategies: library.strategies.filter((_, at) => at !== index),
                  })
                }}
              />
            ))}
          </div>
        )}

      <button
        type="button"
        onClick={() => {
          onChange({
            ...library,
            strategies: [...library.strategies, {
              id: '', name: '', framingStatement: '',
              endUses: [], yieldFraction: 0.25,
              impact: 'low', feasibility: 'medium',
              timing: { kind: 'in_window' },
              prerequisites: [], applicableNaicsPrefixes: [], applicableBuildingTypes: [],
            }],
          })
        }}
        style={{ ...secondaryButton, marginTop: 14, alignSelf: 'flex-start' }}
      >
        Add a strategy
      </button>

      <div
        style={{
          marginTop: 18,
          border: `1.5px solid ${T.color.rule}`,
          background: '#fff',
          padding: '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        <div style={microLabel}>Commit this strategy library</div>
        <p data-testid="library-not-previewable" style={{ ...help, margin: 0, color: T.color.warnInk }}>
          {LIBRARY_NOT_PREVIEWABLE}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label htmlFor={effectiveId} style={microLabel}>Library effective from</label>
          {/*
            Empty, always. Same argument the configuration commit makes: the
            date decides which estimates the new library explains, and a
            pre-filled "today" is a date nobody chose.
          */}
          <input
            id={effectiveId}
            type="date"
            value={effectiveFrom}
            onChange={(event) => { onEffectiveFrom(event.target.value) }}
            style={field}
          />
        </div>

        {problem === null
          ? null
          : (
            <p
              data-testid="library-problem"
              role="alert"
              style={{
                ...help,
                margin: 0,
                border: `1.5px solid ${T.color.red}`,
                background: T.tint.red,
                color: T.color.declinedInk,
                padding: '10px 12px',
              }}
            >
              {problem}
            </p>
          )}

        {committedId === null
          ? null
          : (
            <p
              data-testid="library-committed"
              style={{
                ...help,
                margin: 0,
                border: `1.5px solid ${T.color.green}`,
                background: T.tint.green,
                color: T.color.money,
                padding: '10px 12px',
              }}
            >
              {`Committed as ${committedId}. Nothing was overwritten — every earlier library still `
                + 'explains the lists it was shown for.'}
            </p>
          )}

        {confirming
          ? (
            <div
              data-testid="library-commit-confirm"
              style={{ border: `1.5px solid ${T.color.red}`, background: T.tint.red, padding: '12px 14px' }}
            >
              <p style={{ ...help, margin: 0, color: T.color.declinedInk }}>
                {`This commits ${String(library.strategies.length)} `}
                {library.strategies.length === 1 ? 'strategy' : 'strategies'}
                {` effective ${formatDate(effectiveFrom)}. It changes the ranked list every rep sees `}
                on every account, and no preview has scored it.
              </p>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="button" onClick={onCommit} disabled={busy} style={dangerButton}>
                  Yes, commit this library
                </button>
                <button type="button" onClick={() => { onConfirming(false) }} style={secondaryButton}>
                  Cancel
                </button>
              </div>
            </div>
          )
          : (
            <button
              type="button"
              onClick={() => { onConfirming(true) }}
              disabled={!dirty || effectiveFrom === '' || busy}
              style={{
                ...primaryButton,
                ...(!dirty || effectiveFrom === '' || busy
                  ? {
                      background: T.color.chipOff,
                      border: `1.5px solid ${T.color.chipOff}`,
                      cursor: 'not-allowed',
                    }
                  : {}),
              }}
            >
              Commit strategy library
            </button>
          )}
      </div>
    </div>
  )
}
